import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import Tooltip from "@mui/material/Tooltip";
import ElipsisCell from "../../../../../components/TableBox/Cells/EllipsisCell";
import ElipsisText from "../../../../../components/ElipsisText";
import Checkbox from "../../../../../components/Checkbox";
import Link from "../../../../../components/Link";
import { RowClassName, Modules, RouteParamsTypes } from "../../../../../enums";
import { TableContext } from "../../../../../contexts/TableContext";
import { VALIDATION_FIELD_MAPPING } from "../../../../../utils/appConstant";
import { formatAmount, formatDollarAmount } from "../../../../../utils/methods";
import { CommissionPayoutInvoice } from "../../../../../interfaces/managerStatement.interface";

interface CommissionPayoutsRowProps {
  item: CommissionPayoutInvoice;
  className?: RowClassName;
  selectedIds: number[];
  onSelectToggle: (invoiceId: number) => void;
  onRemove: (invoiceId: number) => void;
  onAdjustClick: (item: CommissionPayoutInvoice) => void;
  onOpenBifurcation: (invoiceId: number) => void;
  readOnly?: boolean;
  saving?: boolean;
}

const CommissionPayoutsRow: React.FC<CommissionPayoutsRowProps> = ({
  item, className, selectedIds, onSelectToggle, onRemove, onAdjustClick, onOpenBifurcation, readOnly, saving,
}) => {
  const { t } = useTranslation();
  const { headCells } = useContext(TableContext);

  const isSelected = selectedIds.includes(item.invoice_id);
  const rowStateClassName = item.needs_review
    ? 'bg-warning-subtle'
    : (item.is_manually_adjusted ? 'bg-primary-subtle' : '');

  return (
    <tr className={clsx(className, rowStateClassName)}>
      {headCells.map((headCell) => {
        if (headCell.id === VALIDATION_FIELD_MAPPING.select) {
          const checkbox = (
            <Checkbox
              variant="primary"
              className="d-checkbox mb-0"
              checked={isSelected}
              disabled={readOnly || item.distance_not_configured}
              name={VALIDATION_FIELD_MAPPING.select}
              onChange={() => onSelectToggle(item.invoice_id)}
            />
          );

          if (item.distance_not_configured) {
            return (
              <td key={headCell.id} width={headCell.width}>
                <Tooltip
                  title={t('reconciliation.managerStatement.detail.pageTexts.detailsTab.commissionPayouts.distanceNotConfiguredTooltip')}
                  arrow
                  classes={{ tooltip: 'tooltip' }}
                >
                  <span>{checkbox}</span>
                </Tooltip>
              </td>
            );
          }

          return (
            <td key={headCell.id} width={headCell.width}>
              {checkbox}
            </td>
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.invoiceNumber) {
          return (
            <ElipsisCell
              key={headCell.id}
              width={headCell.width}
              text={item.invoice_no || '-'}
              node={
                <Link
                  text={item.invoice_no || '-'}
                  href={`${Modules.invoices}${RouteParamsTypes.view}/${item.invoice_id}`}
                  target="_blank"
                />
              }
              className="h6 mb-1 text-decoration-underline hyperlink"
            />
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.invoiceMonth) {
          return (
            <ElipsisCell
              key={headCell.id}
              width={headCell.width}
              text={item.invoice_month || '-'}
            />
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.propertyName) {
          return (
            <ElipsisCell
              key={headCell.id}
              width={headCell.width}
              node={<ElipsisText width="100%" text={item.property_name || '-'} />}
            />
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.coHostCommission) {
          return (
            <ElipsisCell
              key={headCell.id}
              width={headCell.width}
              text={formatDollarAmount(formatAmount(Number(item.co_host_commission || 0).toFixed(2)))}
              className="fw-semibold"
            />
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.baseCommission) {
          return (
            <ElipsisCell
              key={headCell.id}
              width={headCell.width}
              text={formatDollarAmount(formatAmount(Number(item.base_commission || 0).toFixed(2)))}
              className="fw-semibold"
            />
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.propertyVisitCommission) {
          return (
            <ElipsisCell
              key={headCell.id}
              width={headCell.width}
              text={formatDollarAmount(formatAmount(Number(item.property_visit_commission || 0).toFixed(2)))}
              node={(
                <Link
                  text={formatDollarAmount(formatAmount(Number(item.property_visit_commission || 0).toFixed(2)))}
                  onClick={() => onOpenBifurcation(item.invoice_id)}
                />
              )}
              className="fw-semibold text-decoration-underline hyperlink"
            />
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.commissionEarned) {
          return (
            <ElipsisCell
              key={headCell.id}
              width={headCell.width}
              text={formatDollarAmount(formatAmount(Number(item.commission_earned || 0).toFixed(2)))}
              className="fw-semibold"
            />
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.commissionPayoutAction) {
          return (
            <td key={headCell.id} width={headCell.width}>
              <div className="d-flex align-items-center">
                <Tooltip
                  title={t('reconciliation.managerStatement.detail.pageTexts.detailsTab.commissionPayouts.adjustTooltip')}
                  arrow
                  classes={{ tooltip: 'tooltip' }}
                >
                  <span>
                    <button
                      type="button"
                      className="btn btn-sm btn-icon btn-text-secondary waves-effect waves-light rounded-pill"
                      aria-label={t('reconciliation.managerStatement.detail.pageTexts.detailsTab.commissionPayouts.adjustTooltip')}
                      disabled={!isSelected || saving}
                      onClick={() => onAdjustClick(item)}
                    >
                      <i className="ri-edit-box-line ri-20px" />
                    </button>
                  </span>
                </Tooltip>
              </div>
            </td>
          );
        }

        return <ElipsisCell key={headCell.id} width={headCell.width} text="-" />;
      })}
    </tr>
  );
};

export default CommissionPayoutsRow;
